import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BUSINESS, SERVICES, CATEGORIES, WHY_ITEMS, TESTIMONIALS } from '../../data/siteData';
import FadeUp from '../ui/FadeUp';
import SectionHeader from '../ui/SectionHeader';

// ── TRUST BAR ──────────────────────────────────────────────────────────
export function TrustBar() {
  return (
    <div style={{ background: 'var(--orange)', padding: '0.9rem 2rem' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', justifyContent: 'center', gap: '3rem', flexWrap: 'wrap' }}>
        {BUSINESS.trustBadges.map(b => (
          <span key={b} style={{ color: 'white', fontFamily: '"Barlow Condensed"', fontWeight: 700, fontSize: '0.9rem', letterSpacing: '0.07em', textTransform: 'uppercase' }}>{b}</span>
        ))}
      </div>
    </div>
  );
}

// ── SERVICES SECTION ───────────────────────────────────────────────────
export function ServicesSection() {
  return (
    <section id="services" style={{ padding: '5.5rem 2rem', background: 'white' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '3rem', gap: '2rem', flexWrap: 'wrap' }}>
          <FadeUp>
            <SectionHeader tag="What We Do" title="Our Services" />
          </FadeUp>
          <FadeUp>
            <p style={{ fontSize: '1rem', color: 'var(--text-mid)', lineHeight: 1.6, maxWidth: 420 }}>
              From single parts to full vehicle recycling, we offer complete automotive salvage solutions.
            </p>
          </FadeUp>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem' }}>
          {SERVICES.map((svc, i) => (
            <FadeUp key={svc.title} delay={i * 80}>
              <ServiceCard svc={svc} />
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ svc }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Link to={svc.link} style={{ textDecoration: 'none' }}>
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          background: 'var(--ice)', borderRadius: 12, padding: '2rem',
          border: `1.5px solid ${hovered ? 'rgba(13,27,42,0.12)' : 'transparent'}`,
          transform: hovered ? 'translateY(-4px)' : 'none',
          boxShadow: hovered ? '0 12px 40px rgba(13,27,42,0.1)' : 'none',
          transition: 'all 0.25s', position: 'relative', overflow: 'hidden',
          height: '100%',
        }}
      >
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: 3,
          background: 'var(--orange)',
          transform: hovered ? 'scaleX(1)' : 'scaleX(0)',
          transformOrigin: 'left', transition: 'transform 0.3s',
        }} />
        <div style={{ width: 52, height: 52, background: 'var(--navy)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', marginBottom: '1.25rem' }}>
          {svc.icon}
        </div>
        <h3 style={{ fontFamily: '"Barlow Condensed"', fontSize: '1.2rem', fontWeight: 700, color: 'var(--navy)', letterSpacing: '0.03em', marginBottom: '0.5rem' }}>{svc.title}</h3>
        <p style={{ fontSize: '0.9rem', color: 'var(--text-mid)', lineHeight: 1.6 }}>{svc.description}</p>
      </div>
    </Link>
  );
}

// ── CATEGORIES SECTION ─────────────────────────────────────────────────
export function CategoriesSection() {
  const navigate = useNavigate();

  const handleCat = (cat) => {
    navigate(`/inventory?category=${encodeURIComponent(cat.search)}`);
  };

  return (
    <section style={{ padding: '5.5rem 2rem', background: 'var(--navy)' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <FadeUp style={{ marginBottom: '3rem' }}>
          <SectionHeader
            tag="Browse Inventory"
            title="Part Categories"
            subtitle="We stock parts across all major categories for domestic and import vehicles."
            light
          />
        </FadeUp>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
          {CATEGORIES.map((cat, i) => (
            <FadeUp key={cat.name} delay={i * 50}>
              <CategoryCard cat={cat} onClick={() => handleCat(cat)} />
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoryCard({ cat, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.04)',
        border: `1px solid ${hovered ? 'rgba(232,84,26,0.4)' : 'rgba(255,255,255,0.08)'}`,
        borderRadius: 12, padding: '1.75rem 1.5rem',
        cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '1rem',
        transform: hovered ? 'translateY(-3px)' : 'none',
        transition: 'all 0.25s', position: 'relative', overflow: 'hidden',
      }}
    >
      <div style={{
        position: 'absolute', top: '1.25rem', right: '1.25rem',
        color: 'var(--orange)', opacity: hovered ? 1 : 0,
        transform: hovered ? 'translateX(0)' : 'translateX(-8px)',
        transition: 'all 0.25s', fontSize: '1rem',
      }}>→</div>
      <div style={{ fontSize: '2rem' }}>{cat.icon}</div>
      <div>
        <div style={{ fontFamily: '"Barlow Condensed"', fontSize: '1rem', fontWeight: 700, color: 'white', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
          {cat.name}
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--silver)' }}>{cat.desc}</div>
      </div>
    </div>
  );
}

// ── WHY SECTION ────────────────────────────────────────────────────────
export function WhySection() {
  return (
    <section id="why" style={{ padding: '5.5rem 2rem', background: 'white' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5rem', alignItems: 'center' }} className="why-grid">
        {/* Left */}
        <FadeUp>
          <SectionHeader tag="Why Brantford Auto" title={<>Your Trusted<br />Local Parts Source</>} subtitle="For over two decades, we've been the go-to salvage yard for drivers, mechanics, and shops across Brantford, Paris, and all of Brant County." />
          <div style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {WHY_ITEMS.map(item => (
              <WhyItem key={item.title} item={item} />
            ))}
          </div>
        </FadeUp>

        {/* Right image */}
        <FadeUp delay={150}>
          <div style={{ position: 'relative' }}>
            <div style={{ borderRadius: 16, overflow: 'hidden', aspectRatio: '4/3', background: 'var(--navy-mid)' }}>
              <img src="https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800&q=80" alt="Auto salvage yard" loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'saturate(0.8)' }} />
            </div>
            <div style={{
              position: 'absolute', bottom: '-1.5rem', right: '-1.5rem',
              background: 'var(--orange)', color: 'white', borderRadius: 12,
              padding: '1.25rem 1.5rem', textAlign: 'center',
              boxShadow: '0 8px 32px rgba(232,84,26,0.4)',
            }}>
              <div style={{ fontFamily: '"Bebas Neue"', fontSize: '2.5rem', lineHeight: 1 }}>20+</div>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Years<br />Serving ON</div>
            </div>
          </div>
        </FadeUp>
      </div>
      <style>{`
        @media (max-width: 860px) { .why-grid { grid-template-columns: 1fr !important; gap: 3rem !important; } }
      `}</style>
    </section>
  );
}

function WhyItem({ item }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: '1rem', padding: '1.25rem',
        borderRadius: 10, border: `1.5px solid ${hovered ? 'rgba(13,27,42,0.2)' : 'var(--silver-light)'}`,
        boxShadow: hovered ? '0 4px 20px rgba(13,27,42,0.06)' : 'none',
        transition: 'all 0.2s',
      }}
    >
      <div style={{ width: 44, height: 44, flexShrink: 0, background: 'rgba(232,84,26,0.1)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem' }}>
        {item.icon}
      </div>
      <div>
        <h4 style={{ fontFamily: '"Barlow Condensed"', fontSize: '1rem', fontWeight: 700, color: 'var(--navy)', letterSpacing: '0.03em', marginBottom: '0.2rem' }}>{item.title}</h4>
        <p style={{ fontSize: '0.88rem', color: 'var(--text-mid)', lineHeight: 1.5 }}>{item.desc}</p>
      </div>
    </div>
  );
}

// ── TESTIMONIALS ───────────────────────────────────────────────────────
export function TestimonialsSection() {
  return (
    <section style={{ padding: '5.5rem 2rem', background: 'var(--ice)' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <FadeUp style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <SectionHeader tag="Customer Reviews" title="What Our Customers Say" subtitle="Hundreds of happy customers throughout Brantford, Paris, and Brant County." center />
        </FadeUp>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
          {TESTIMONIALS.map((t, i) => (
            <FadeUp key={t.name} delay={i * 60}>
              <TestimonialCard t={t} />
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialCard({ t }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'white', borderRadius: 14, padding: '2rem',
        boxShadow: hovered ? '0 12px 36px rgba(13,27,42,0.12)' : '0 2px 20px rgba(13,27,42,0.06)',
        border: '1px solid rgba(13,27,42,0.05)',
        transform: hovered ? 'translateY(-4px)' : 'none',
        transition: 'all 0.25s', height: '100%',
      }}
    >
      <div style={{ color: '#f59e0b', fontSize: '0.95rem', marginBottom: '1rem', letterSpacing: '0.05em' }}>
        {'★'.repeat(t.stars)}{'☆'.repeat(5 - t.stars)}
      </div>
      <p style={{ fontSize: '0.95rem', fontStyle: 'italic', color: 'var(--text-mid)', lineHeight: 1.7, marginBottom: '1.5rem' }}>"{t.text}"</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"Bebas Neue"', fontSize: '1rem', color: 'white', flexShrink: 0 }}>
          {t.initials}
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--navy)' }}>{t.name}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>{t.location}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.7rem', color: 'var(--text-light)', marginTop: '0.1rem' }}>
            <span style={{ width: 14, height: 14, borderRadius: '50%', background: '#4285f4', color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.55rem' }}>G</span>
            Google Review
          </div>
        </div>
      </div>
    </div>
  );
}

// ── CTA BANNER ─────────────────────────────────────────────────────────
export function CtaBanner() {
  return (
    <section style={{ background: 'linear-gradient(135deg, var(--navy) 0%, var(--navy-light) 100%)', padding: '5rem 2rem', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
      <div className="grid-overlay" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundSize: '40px 40px' }} />
      <FadeUp style={{ position: 'relative', zIndex: 1 }}>
        <h2 style={{ fontFamily: '"Bebas Neue"', fontSize: 'clamp(2rem, 5vw, 3.5rem)', color: 'white', letterSpacing: '0.03em', marginBottom: '0.75rem' }}>
          Looking for a Specific Part?<br />We'll Find It For You.
        </h2>
        <p style={{ color: 'var(--silver-light)', fontSize: '1.05rem', marginBottom: '2.5rem', maxWidth: 500, margin: '0 auto 2.5rem' }}>
          Can't find it in our yard? Our parts locator network connects us with salvage yards across Ontario.
        </p>
        <Link to="/request-part" className="btn-primary" style={{ fontSize: '1.05rem', padding: '1rem 2.5rem' }}>
          📋 Request a Part
        </Link>
        <div style={{ marginTop: '1.5rem' }}>
          <a href={`tel:${BUSINESS.phoneRaw}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: 'var(--silver-light)', fontSize: '0.95rem', textDecoration: 'none', transition: 'color 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.color = 'white'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--silver-light)'}
          >
            📞 Or call us directly: {BUSINESS.phone}
          </a>
        </div>
      </FadeUp>
    </section>
  );
}
